import type { DegreeType } from '@antv/algorithm/es/types'
import type { WritableDraft } from 'immer/dist/internal'
import { uniqBy } from 'lodash-es'
import { Edge, Graph, Node } from '../../entities'
import { LayoutController } from '../../extensions'
import type { GraphStoreContext, NormalGraphStoreContext } from '../../store'
import type {
  AbstractGraph,
  EdgeData,
  ElementIdentifier,
  NodeData,
  NormalGraphData,
  NormalGraphLayoutConfig,
  NormalGraphOptions,
} from '../../types'
import { EVENT_NAME } from '../../types'
import { isValidEdge } from '../../utils'
import { NormalGraphEdge } from './normal-graph-edge'
import { NormalGraphNode } from './normal-graph-node'

export class NormalGraph
  extends Graph<NormalGraphNode, NormalGraphEdge>
  implements AbstractGraph
{
  layoutController: LayoutController
  /**
   * 度
   */
  degrees?: DegreeType

  constructor(options: NormalGraphOptions) {
    super(options, {
      graphType: 'normal',
    })

    this.layoutController = new LayoutController(
      this,
      options.layout as NormalGraphLayoutConfig
    )

    this.once(EVENT_NAME.afterinit, () => {
      this.service.send({
        type: 'SET_LAYOUT_CONTEXT',
        data: {
          layoutAlgorithm: this.layoutController.execute,
          modelFactory: this.modelFactory,
        },
      })

      if (options.data) {
        this.changeData(options.data)
      }
    })
  }

  /**
   * 设置数据
   *
   * @param data
   * @param stack
   */
  changeData = (data: NormalGraphData, stack = true) => {
    this.service.send({
      type: 'CHANGE_DATA',
      data: {
        graphData: data,
      },
    })

    // 数据改变后自动布局
    this.layout()
  }

  /**
   * 以当前配置执行一次布局
   */
  layout = () => {
    this.service.send({
      type: 'EXECUTE_LAYOUT',
    })
  }

  /**
   * 添加节点
   *
   * @param nodeData
   * @param stack
   */
  addNode = (nodeData: NodeData, stack = true) => {
    this.service.send({
      type: 'ADD_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as NormalGraphStoreContext

          context.graphData.nodes.push(nodeData)
        },
      },
    })
  }

  /**
   * 删除节点
   *
   * @param nodeOrNodeId
   * @param stack
   */
  removeNode = (
    nodeOrNodeId: ElementIdentifier | NormalGraphNode,
    stack = true
  ) => {
    this.service.send({
      type: 'REMOVE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as NormalGraphStoreContext
          const id = Node.isNode(nodeOrNodeId) ? nodeOrNodeId.id : nodeOrNodeId
          const index = context.nodes.findIndex((node) => node.id === id)

          context.graphData?.nodes.splice(index, 1)
        },
      },
    })
  }

  /**
   * 根据一个谓词函数删除节点
   *
   * @param predicate
   */
  removeNodeBy = (predicate: (node: Node) => boolean) => {
    this.service.send({
      type: 'REMOVE_NODE_BY',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as NormalGraphStoreContext

          if (context.graphData) {
            context.graphData.nodes = context.graphData.nodes.filter((node) => {
              const nodeModel = context.nodes.find(({ id }) => node.id === id)

              if (nodeModel) {
                return !predicate(nodeModel)
              }

              return true
            })
          }
        },
      },
    })
  }

  /**
   * 更新节点
   *
   * @param nodeOrNodeId
   * @param nodeData
   * @param stack
   */
  override updateNode(
    nodeOrNodeId: ElementIdentifier | NormalGraphNode,
    nodeData: NodeData,
    stack = true
  ) {
    const id = Node.isNode(nodeOrNodeId) ? nodeOrNodeId.id : nodeOrNodeId
    // 是否需要重新布局
    const needLayout = nodeData.id != null && nodeData.id !== id

    if (needLayout) {
      this.service.send({
        type: 'UPDATE_NODE',
        data: {
          producer: (_context: WritableDraft<GraphStoreContext>) => {
            const context = _context as NormalGraphStoreContext
            const targetNode = context.graphData?.nodes.find(
              (node) => node.id === id
            )

            if (targetNode) {
              Object.assign(targetNode, nodeData)
            }
          },
        },
      })
    } else {
      this.service.send({
        type: 'UPDATE_NODE_PROPERTIES',
        data: {
          id,
          nodeData,
        },
      })
    }
  }

  /**
   * 添加边
   *
   * @param edgeData
   * @param stack
   */
  addEdge = (edgeData: EdgeData, stack = true) => {
    this.service.send({
      type: 'ADD_EDGE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as NormalGraphStoreContext

          context.graphData?.edges.push(edgeData)
        },
      },
    })
  }

  /**
   * 删除边
   *
   * @param edgeOrEdgeId
   * @param stack
   */
  removeEdge = (
    edgeOrEdgeId: ElementIdentifier | NormalGraphEdge,
    stack = true
  ) => {
    this.service.send({
      type: 'REMOVE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as NormalGraphStoreContext
          const id = Edge.isEdge(edgeOrEdgeId) ? edgeOrEdgeId.id : edgeOrEdgeId

          context.graphData.edges = context.graphData.edges.filter(
            (edge) => edge.id !== id
          )
        },
      },
    })
  }

  /**
   * 根据一个谓词函数删除节点
   *
   * @param predicate
   */
  removeEdgeBy = (predicate: (edge: Edge) => boolean) => {
    this.service.send({
      type: 'REMOVE_EDGE_BY',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as NormalGraphStoreContext

          if (context.graphData) {
            context.graphData.edges = context.graphData.edges.filter((edge) => {
              const edgeModel = context.edges.find(({ id }) => edge.id === id)

              if (edgeModel) {
                return !predicate(edgeModel)
              }

              return true
            })
          }
        },
      },
    })
  }

  /**
   * 更新边
   *
   * @param edgeOrEdgeId
   * @param edgeData
   * @param stack
   */
  override updateEdge(
    edgeOrEdgeId: ElementIdentifier | NormalGraphEdge,
    edgeData: EdgeData,
    stack = true
  ) {
    const id = Edge.isEdge(edgeOrEdgeId)
      ? edgeOrEdgeId.id
      : (edgeOrEdgeId as string)
    // 是否更新了边关系，需要重新布局
    const needLayout = edgeData.source != null || edgeData.target != null

    if (needLayout) {
      this.service.send({
        type: 'UPDATE_EDGE',
        data: {
          producer: (_context: WritableDraft<GraphStoreContext>) => {
            const context = _context as NormalGraphStoreContext
            const targetEdgeModel = context.edges.find((edge) => edge.id === id)
            const targetEdge = context.graphData?.edges.find(
              (edge) =>
                edge.source === targetEdgeModel?.source &&
                edge.target === targetEdgeModel?.target
            )

            if (targetEdge) {
              Object.assign(targetEdge, edgeData)
            }
          },
        },
      })
    } else {
      this.service.send({
        type: 'UPDATE_EDGE_PROPERTIES',
        data: {
          id,
          edgeData,
        },
      })
    }
  }

  /**
   * 显示节点
   *
   * @param node
   * @param stack
   */
  showNode = (node: ElementIdentifier | NormalGraphNode, stack = true) => {}

  /**
   * 隐藏节点
   *
   * @param node
   * @param stack
   */
  hideNode = (node: ElementIdentifier | NormalGraphNode, stack = true) => {}

  /**
   * 销毁布局方法，用在不需要布局的场景
   */
  destroyLayout = () => {
    this.layoutController.destroy()
  }

  /**
   * 销毁实例
   */
  override destroy() {
    super.destroy()

    this.destroyLayout()
  }

  /**
   * 获取 node 的度数
   *
   * @param {(string | Node)} nodeOrNodeId 节点 ID 或实例
   * @param {('in' | 'out' | 'total' | 'all' | undefined)} type，in 入度，out 出度，total 总度数，all 返回三种类型度数的对象
   * @param refresh
   * @returns {Number | Object} 该节点的度数
   * @memberof IAbstractGraph
   */
  getNodeDegree(
    nodeOrNodeId: string | Node,
    type: 'in' | 'out' | 'total' | 'all' | undefined = undefined,
    refresh: boolean = false
  ): number | object {
    let node: Node | undefined

    if (!Node.isNode(nodeOrNodeId)) {
      node = this.findNodeById(nodeOrNodeId)
    } else {
      node = nodeOrNodeId
    }

    if (!node) {
      throw new Error('node not found')
    }

    if (!this.degrees || refresh) {
      this.degrees = Graph.Algorithms.getDegree(this.exportData())
    }

    const nodeDegrees = this.degrees[node.id]

    let res = 0

    // 如果是通过 addItem 后面新增加的节点，此时它的所有度数都为 0
    if (!nodeDegrees) {
      return 0
    }

    switch (type) {
      case 'in':
        res = nodeDegrees.inDegree
        break
      case 'out':
        res = nodeDegrees.outDegree
        break
      case 'all':
        res = nodeDegrees.degree
        break
      default:
        res = nodeDegrees.degree
        break
    }

    return res
  }

  /**
   * 根据图数据创建模型
   *
   * @param data
   * @returns
   */
  private modelFactory = (data: NormalGraphData) => {
    const normalized = this._normalizeData(data)
    const nodes = normalized.nodes.map((node) => this.createNode(node)) ?? []
    const edges = normalized.edges.map((edge) => this.createEdge(edge)) ?? []
    const positions = normalized.nodes.reduce<Record<string, [number, number]>>(
      (acc, { id, x, y }) => ({
        ...acc,
        [id]: [x || 0, y || 0],
      }),
      {}
    )

    return {
      nodes,
      edges,
      positions,
    }
  }

  /**
   * 根据布局类型创建节点
   *
   * @param data
   * @private
   */
  private createNode = (data: NodeData) => new NormalGraphNode(this, data)

  /**
   * 根据布局类型创建边
   *
   * @param data
   * @private
   */
  private createEdge = (data: EdgeData) => new NormalGraphEdge(this, data)

  /**
   * 标准化数据
   * @description 移除不合法的边
   * TODO: 警告不合法的数据
   */
  private _normalizeData = (
    data?: NormalGraphData
  ): { nodes: NodeData[]; edges: EdgeData[] } => {
    const { nodes, edges } = data ?? {
      nodes: [],
      edges: [],
    }
    const nextNodes = uniqBy(nodes, 'id')
    const nextEdges = edges.filter(isValidEdge(nextNodes))

    return {
      nodes: nextNodes,
      edges: nextEdges,
    }
  }
}
